import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, Home, ChevronRight, Phone, Heart, AlertTriangle, Brain,
  MessageCircle, Target, FileText, ClipboardList, User, Lock, ShieldAlert, Utensils, Wallet,
} from 'lucide-react';
import { secureStore, TTL } from '@/lib/offline/secureStore';
import { MobileBottomNav } from '@/components/mobile/MobileNavigation';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';

function Section({ title, icon: Icon, children, testid, tone = 'default' }) {
  const tones = {
    default: 'text-primary',
    danger: 'text-red-500',
    warning: 'text-amber-500',
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`h-4 w-4 ${tones[tone]}`} />
          <p className="text-sm font-semibold">{title}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

const CATEGORY_LABELS = {
  BEHAVIOUR_SUPPORT_PLAN: 'Behaviour Support Plan',
  RISK_ASSESSMENT_DOC: 'Risk Assessment',
  NDIS_PLAN: 'NDIS Plan',
  MEDICAL: 'Medical',
};

export default function MobileParticipantProfilePage() {
  const navigate = useNavigate();
  const { participantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [p, setP] = useState(null);

  const cacheKey = `participant_profile:${participantId}`;

  const load = useCallback(async () => {
    setLoading(true); setForbidden(false); setNotFound(false);
    try {
      const res = await api.get(`/mobile/participants/${participantId}/quick-profile`);
      const data = res.data?.data || res.data;
      setP(data); setFromCache(false);
      try { await secureStore.setItem(cacheKey, data, TTL.SHIFT_META); } catch (_) { /* noop */ }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) setForbidden(true);
      else if (status === 404) setNotFound(true);
      else {
        let snap = null;
        try { snap = await secureStore.getItem(cacheKey); } catch (_) { /* noop */ }
        if (snap) { setP(snap); setFromCache(true); toast.info('Showing saved offline copy'); }
        else toast.error('Could not load participant profile. Check your connection.');
      }
    } finally {
      setLoading(false);
    }
  }, [participantId, cacheKey]);

  useEffect(() => { load(); }, [load]);

  if (forbidden) {
    return (
      <MobileAccessDenied
        title="Participant not assigned"
        message="You can only view participants assigned to your shifts. Contact your coordinator if you believe this is a mistake."
        onBack={() => navigate(-1)}
      />
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center" data-testid="participant-not-found">
        <User className="h-12 w-12 text-muted-foreground mb-4" />
        <h1 className="text-lg font-semibold mb-2">Participant not found</h1>
        <Button onClick={() => navigate(-1)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Go back</Button>
      </div>
    );
  }

  const initials = (p?.name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const hasContacts = (p?.emergencyContacts || []).length > 0;

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-participant-profile">
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="-ml-2" onClick={() => navigate(-1)} data-testid="profile-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <button onClick={() => navigate('/mobile')} className="flex items-center gap-1 hover:text-foreground">
                <Home className="h-3 w-3" /> Home
              </button>
              <ChevronRight className="h-3 w-3" /> <span>Participant</span>
            </div>
            <h1 className="text-base font-bold truncate">{p?.preferredName || p?.name || 'Profile'}</h1>
          </div>
          {p?.readOnly && (
            <Badge variant="secondary" className="text-[10px] gap-1" data-testid="readonly-badge"><Lock className="h-3 w-3" /> Read-only</Badge>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-4 space-y-4" data-testid="profile-skeleton">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      ) : !p ? (
        <div className="p-6 text-center text-muted-foreground">Unable to load profile.</div>
      ) : (
        <div className="p-4 space-y-4">
          {fromCache && (
            <div className="flex items-center gap-2 rounded-xl bg-blue-500/10 border border-blue-500/20 px-4 py-3 text-blue-700 text-sm" data-testid="profile-offline-banner">
              <ShieldAlert className="h-4 w-4" /> Offline — showing a saved copy of this profile.
            </div>
          )}

          {/* Identity header */}
          <Card data-testid="profile-header">
            <CardContent className="p-4 flex items-center gap-4">
              {p.photoUrl ? (
                <img src={p.photoUrl} alt={p.name} className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-lg font-bold">{initials}</div>
              )}
              <div className="min-w-0">
                <p className="font-bold text-lg truncate">{p.preferredName || p.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {p.name}{p.pronouns ? ` · ${p.pronouns}` : ''}{p.age ? ` · ${p.age}y` : ''}
                </p>
                {p.culturalBackground && <p className="text-xs text-muted-foreground">{p.culturalBackground}</p>}
              </div>
            </CardContent>
          </Card>

          {/* Care Plan Brief CTA (Day 21) */}
          <Button
            variant="outline"
            className="w-full justify-between"
            onClick={() => navigate(`/mobile/participants/${participantId}/care-plan`)}
            data-testid="view-care-plan-brief-btn"
          >
            <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" /> View full Care Plan Brief</span>
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Emergency contacts — prominent, easy to access */}
          <Section title="Emergency Contacts" icon={Phone} testid="profile-contacts" tone="danger">
            {!hasContacts ? (
              <p className="text-xs text-muted-foreground">No emergency contacts on file.</p>
            ) : (
              <div className="space-y-2">
                {p.emergencyContacts.map((c, i) => (
                  <div key={i} className="flex items-center justify-between" data-testid="profile-contact-item">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.name || c.label}</p>
                      <p className="text-xs text-muted-foreground">{c.label}{c.phone ? ` · ${c.phone}` : ''}</p>
                    </div>
                    {c.phone && (
                      <a href={`tel:${c.phone}`} data-testid="profile-contact-call">
                        <Button size="sm" className="gap-1"><Phone className="h-4 w-4" /> Call</Button>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Allergies & medical alerts */}
          <Section title="Allergies & Medical Alerts" icon={AlertTriangle} testid="profile-medical" tone="danger">
            <div className="space-y-1 text-sm">
              <p><span className="text-muted-foreground">Allergies: </span>{p.allergies || 'None recorded'}</p>
              <p><span className="text-muted-foreground">Medical alerts: </span>{p.medicalAlerts || 'None recorded'}</p>
              {p.dietaryRequirements && (
                <p className="flex items-center gap-1"><Utensils className="h-3 w-3 text-amber-500" /> {p.dietaryRequirements}</p>
              )}
            </div>
          </Section>

          {/* Support needs */}
          <Section title="Support Needs" icon={Heart} testid="profile-support-needs">
            {(p.supportNeeds || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No specific support needs recorded.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {p.supportNeeds.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s}</Badge>)}
              </div>
            )}
          </Section>

          {/* Behaviour strategies */}
          <Section title="Behaviour Support & Triggers" icon={Brain} testid="profile-behaviour" tone="warning">
            <p className="text-sm whitespace-pre-wrap">{p.behaviourStrategies || 'No behaviour support strategies recorded.'}</p>
          </Section>

          {/* Communication preferences */}
          <Section title="Communication Preferences" icon={MessageCircle} testid="profile-communication">
            <div className="text-sm space-y-1">
              <p>{p.communicationPreference || 'Not recorded'}</p>
              {(p.primaryLanguage || p.interpreterRequired) && (
                <p className="text-xs text-muted-foreground">
                  {p.primaryLanguage ? `Language: ${p.primaryLanguage}` : ''}
                  {p.interpreterRequired ? ' · Interpreter required' : ''}
                </p>
              )}
            </div>
          </Section>

          {/* Care plan summary */}
          {p.carePlanSummary && (
            <Section title="Care Plan Summary" icon={ClipboardList} testid="profile-careplan">
              <div className="text-sm space-y-1">
                {p.carePlanSummary.title && <p className="font-medium">{p.carePlanSummary.title}</p>}
                {p.carePlanSummary.morning && <p><span className="text-muted-foreground">Morning: </span>{p.carePlanSummary.morning}</p>}
                {p.carePlanSummary.afternoon && <p><span className="text-muted-foreground">Afternoon: </span>{p.carePlanSummary.afternoon}</p>}
                {p.carePlanSummary.evening && <p><span className="text-muted-foreground">Evening: </span>{p.carePlanSummary.evening}</p>}
                {p.carePlanSummary.night && <p><span className="text-muted-foreground">Night: </span>{p.carePlanSummary.night}</p>}
                {p.importantNotes && <p className="pt-1 text-amber-700">{p.importantNotes}</p>}
                {p.carePlanSummary.reviewDueDate && <p className="text-[11px] text-muted-foreground pt-1">Review due: {p.carePlanSummary.reviewDueDate}</p>}
              </div>
            </Section>
          )}

          {/* Goals */}
          <Section title="Goals" icon={Target} testid="profile-goals">
            {(p.goals || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No active goals.</p>
            ) : (
              <ul className="space-y-2">
                {p.goals.map((g) => (
                  <li key={g.id} className="text-sm" data-testid="profile-goal-item">
                    <p className="font-medium">{g.title}</p>
                    <p className="text-xs text-muted-foreground">{g.category}{g.progress ? ` · ${g.progress.replace('_', ' ')}` : ''}</p>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Important documents (read-only metadata) */}
          <Section title="Important Documents" icon={FileText} testid="profile-documents">
            {(p.documents || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No documents available.</p>
            ) : (
              <ul className="space-y-2">
                {p.documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between text-sm" data-testid="profile-document-item">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{d.title}</p>
                      <p className="text-xs text-muted-foreground">{CATEGORY_LABELS[d.category] || d.category}</p>
                    </div>
                    {d.expiryDate && <Badge variant="outline" className="text-[10px]">exp {String(d.expiryDate).slice(0, 10)}</Badge>}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Funding & NDIS — only present in the unredacted (reviewer) payload */}
          {!p.redacted && (p.ndisNumber || p.funding) && (
            <Section title="Funding & NDIS" icon={Wallet} testid="profile-funding">
              <div className="text-sm space-y-1">
                {p.ndisNumber && <p><span className="text-muted-foreground">NDIS number: </span>{p.ndisNumber}</p>}
                {p.planManagementType && <p><span className="text-muted-foreground">Plan management: </span>{p.planManagementType}</p>}
                {p.funding?.totalBudget != null && <p><span className="text-muted-foreground">Total budget: </span>${p.funding.totalBudget}</p>}
                {p.funding?.remainingBudget != null && <p><span className="text-muted-foreground">Remaining: </span>${p.funding.remainingBudget}</p>}
              </div>
            </Section>
          )}
        </div>
      )}

      <MobileBottomNav />
    </div>
  );
}
